/**
 * Attached files in the protocol PDF: read once per render, then printed as
 * thumbnails in the issue table and/or as full pages in the appendix. Every
 * file carries its own caption — there is no separate index.
 */
import { PDFDocument, degrees, type PDFEmbeddedPage, type PDFImage, type PDFPage } from "pdf-lib";
import { toWinAnsi, wrapText } from "@/domains/documents/utils/templateFormPdfRenderer";
import { fill } from "@/lib/i18n/fill";
import {
  CONTENT_WIDTH,
  MARGIN,
  MUTED,
  PAGE_HEIGHT,
  PAGE_WIDTH,
  RULE,
  SURFACE,
  TEXT,
  addPage,
  capLines,
  drawLines,
  drawSectionHeading,
  drawText,
  ellipsise,
  ensureSpace,
  linesPerPage,
  type PdfSheet,
} from "@/domains/pm/utils/protocolPdfSheet";
import type { Pacer } from "@/domains/pm/utils/protocolPdfPacer";
import { PAPERCLIP_SIZE } from "@/domains/pm/utils/protocolPdfLinks";
import type {
  ProtocolDocumentAttachmentFile,
  ProtocolDocumentModel,
} from "@/domains/pm/utils/protocol";

const GROUP_HEADING_SIZE = 10;
const GROUP_HEADING_LINE_HEIGHT = 13;
const CAPTION_HEIGHT = 18;
const NOTE_HEIGHT = 14;
const ITEM_GAP = 16;
const LIST_LINE_HEIGHT = 14;
/** The caption strip over an attached PDF's page, at A4 width. */
const STRIP_HEIGHT = 18;
/** Bottom margin plus footer — what `ensureSpace` keeps clear. */
const PAGE_FOOT = MARGIN + 20;
/** The tallest an image may print: a fresh page under its caption. */
const MAX_IMAGE_HEIGHT = PAGE_HEIGHT - MARGIN - PAGE_FOOT - CAPTION_HEIGHT;

/** One page of an attached PDF, embedded, and the turn its viewer shows it at. */
interface AttachedPage {
  embedded: PDFEmbeddedPage;
  /** The page's /Rotate, clockwise: an embedded page is drawn unrotated. */
  rotation: 0 | 90 | 180 | 270;
}

/** A file once its bytes have been read: what prints for it. */
export type ResolvedAttachment = { filename: string } & (
  | { kind: "image"; image: PDFImage }
  /** Every page when the appendix prints it; only the first for a thumbnail. */
  | { kind: "pdf"; pages: AttachedPage[] }
  | { kind: "unsupported" }
  | { kind: "failed" }
);

export type ResolvedAttachments = ReadonlyMap<number, ResolvedAttachment>;

/**
 * Read every file before anything is drawn, so neither the table nor the
 * appendix promises a picture the file then fails to deliver. A protected or
 * damaged PDF, or image bytes pdf-lib refuses, is known as failed up front.
 * An image is embedded once and drawn wherever it appears.
 */
export async function resolveAttachments(
  sheet: PdfSheet,
  model: ProtocolDocumentModel,
  pace: Pacer
): Promise<ResolvedAttachments> {
  const inAppendix = new Set(model.attachments?.groups.flatMap((group) => group.numbers) ?? []);
  const resolved = new Map<number, ResolvedAttachment>();
  for (const file of model.attachments?.files ?? []) {
    resolved.set(file.number, await resolveFile(sheet, file, inAppendix.has(file.number)));
    await pace();
  }
  return resolved;
}

/** The crop box is what a viewer shows; pdf-lib's default is the media box
 *  measured from the origin, which shifts a page whose box does not start there. */
function cropBoundingBox(page: PDFPage) {
  const { x, y, width, height } = page.getCropBox();
  return { left: x, bottom: y, right: x + width, top: y + height };
}

function pageRotation(page: PDFPage): AttachedPage["rotation"] {
  const angle = ((page.getRotation().angle % 360) + 360) % 360;
  return angle === 90 || angle === 180 || angle === 270 ? angle : 0;
}

async function resolveFile(
  sheet: PdfSheet,
  file: ProtocolDocumentAttachmentFile,
  allPages: boolean
): Promise<ResolvedAttachment> {
  const { content, filename } = file;
  try {
    switch (content.type) {
      case "image": {
        const image =
          content.format === "png"
            ? await sheet.doc.embedPng(content.bytes)
            : await sheet.doc.embedJpg(content.bytes);
        return { filename, kind: "image", image };
      }
      case "pdf": {
        // Encrypted documents are refused rather than loaded with
        // `ignoreEncryption`: their pages come out blank or garbled, which
        // would print as if the attachment had been included.
        const pdf = await PDFDocument.load(content.bytes, { updateMetadata: false });
        const sources = allPages ? pdf.getPages() : pdf.getPages().slice(0, 1);
        if (sources.length === 0) return { filename, kind: "failed" };
        // pdf-lib decodes an embedded page only when the document is saved, and
        // a page it cannot decode (an unsupported filter, no content stream)
        // then fails the save — the whole protocol, not this one file. And once
        // registered, a failed page stays on the document's list. So each page
        // is embedded into a throwaway document first, through the same code.
        const probe = await PDFDocument.create();
        for (const source of sources) {
          await (await probe.embedPage(source, cropBoundingBox(source))).embed();
        }
        const pages: AttachedPage[] = [];
        for (const source of sources) {
          const embedded = await sheet.doc.embedPage(source, cropBoundingBox(source));
          pages.push({ embedded, rotation: pageRotation(source) });
        }
        return { filename, kind: "pdf", pages };
      }
      case "unsupported":
        return { filename, kind: "unsupported" };
      case "failed":
        return { filename, kind: "failed" };
    }
  } catch (error) {
    console.warn(`Protocol attachment ${file.number} could not be included`, error);
    return { filename, kind: "failed" };
  }
}

/** The page as a viewer shows it: its size once turned, and how to draw it into a box. */
function shownPage(page: AttachedPage): {
  width: number;
  height: number;
  drawAt: (box: Box) => void;
} {
  const { embedded, rotation } = page;
  const turned = rotation === 90 || rotation === 270;
  return {
    width: turned ? embedded.height : embedded.width,
    height: turned ? embedded.width : embedded.height,
    // Rotated about its origin by pdf-lib (anticlockwise), so the origin moves
    // to the corner that ends up bottom-left of the box.
    drawAt: ({ page: target, x, y, width, height }) => {
      const origin = {
        0: { x, y },
        90: { x, y: y + height },
        180: { x: x + width, y: y + height },
        270: { x: x + width, y },
      }[rotation];
      target.drawPage(embedded, {
        ...origin,
        width: turned ? height : width,
        height: turned ? width : height,
        rotate: degrees(-rotation),
      });
    },
  };
}

export function countNotIncluded(resolved: ResolvedAttachments): number {
  return Array.from(resolved.values()).filter((entry) => entry.kind === "failed").length;
}

// ---------------------------------------------------------------------------
// The issue table's thumbnail cell
// ---------------------------------------------------------------------------

/** Tall enough that a site photo reads at a glance, not just shows there is one. */
const THUMB_MAX_HEIGHT = 84;
const THUMB_GAP = 4;
const THUMB_NAME_SIZE = 6.5;
const THUMB_NAME_HEIGHT = 9;
/** More than this and the cell says "+N" — a row is a summary, the appendix the record. */
const MAX_THUMBS = 3;

type Thumb =
  | {
      type: "picture";
      /** The file it shows — what a click on it opens in the appendix. */
      number: number;
      draw: (x: number, y: number, w: number, h: number) => void;
      w: number;
      h: number;
    }
  | { type: "name"; text: string };

/** The picture a thumbnail draws: an image, or a PDF's embedded first page. */
function thumbSource(
  entry: ResolvedAttachment
): { width: number; height: number; drawAt: (box: Box) => void } | null {
  if (entry.kind === "image") {
    const { image } = entry;
    return {
      width: image.width,
      height: image.height,
      drawAt: (box) => box.page.drawImage(image, box),
    };
  }
  if (entry.kind === "pdf") return shownPage(entry.pages[0]);
  return null;
}

type Box = { page: PDFPage; x: number; y: number; width: number; height: number };

function thumbFor(
  sheet: PdfSheet,
  number: number,
  entry: ResolvedAttachment,
  width: number
): Thumb {
  const source = thumbSource(entry);
  if (!source) return { type: "name", text: entry.filename };
  const scale = Math.min(width / source.width, THUMB_MAX_HEIGHT / source.height, 1);
  return {
    type: "picture",
    number,
    w: source.width * scale,
    h: source.height * scale,
    // Reads `sheet.page` when drawn, not when measured: a page break can land
    // between the two.
    draw: (x, y, dw, dh) => {
      const { page } = sheet;
      source.drawAt({ page, x, y, width: dw, height: dh });
      page.drawRectangle({ x, y, width: dw, height: dh, borderColor: RULE, borderWidth: 0.4 });
    },
  };
}

/** The cell's contents, laid out top to bottom, and the height they take. */
export function measureThumbCell(
  sheet: PdfSheet,
  numbers: readonly number[],
  resolved: ResolvedAttachments,
  width: number
): { thumbs: Thumb[]; more: number; height: number } {
  const entries = numbers.flatMap((number) => {
    const entry = resolved.get(number);
    return entry ? [{ number, entry }] : [];
  });
  const thumbs = entries
    .slice(0, MAX_THUMBS)
    .map(({ number, entry }) => thumbFor(sheet, number, entry, width));
  const more = entries.length - thumbs.length;
  let height = 0;
  thumbs.forEach((thumb, index) => {
    if (index > 0) height += THUMB_GAP;
    height += thumb.type === "picture" ? thumb.h : THUMB_NAME_HEIGHT;
  });
  if (more > 0) height += THUMB_GAP + THUMB_NAME_HEIGHT;
  return { thumbs, more, height };
}

/** Draws the measured cell from `top` downwards, starting at `x`. */
export function drawThumbCell(
  sheet: PdfSheet,
  cell: ReturnType<typeof measureThumbCell>,
  x: number,
  top: number,
  width: number
): void {
  let y = top;
  cell.thumbs.forEach((thumb, index) => {
    if (index > 0) y -= THUMB_GAP;
    if (thumb.type === "picture") {
      thumb.draw(x, y - thumb.h, thumb.w, thumb.h);
      sheet.links.toAttachment.push({
        spot: { page: sheet.page, x, y: y - thumb.h, width: thumb.w, height: thumb.h },
        number: thumb.number,
      });
      y -= thumb.h;
    } else {
      drawText(sheet, ellipsise(thumb.text, sheet.regular, THUMB_NAME_SIZE, width), x, y - 7, {
        size: THUMB_NAME_SIZE,
        color: MUTED,
      });
      y -= THUMB_NAME_HEIGHT;
    }
  });
  if (cell.more > 0) {
    drawText(sheet, `+${cell.more}`, x, y - THUMB_GAP - 7, {
      font: sheet.bold,
      size: THUMB_NAME_SIZE + 0.5,
      color: MUTED,
    });
  }
}

// ---------------------------------------------------------------------------
// The appendix
// ---------------------------------------------------------------------------

/** A heading's wrapped lines and the height it takes, chrome included. */
function measureGroupHeading(
  sheet: PdfSheet,
  heading: string
): { lines: string[]; height: number } {
  const lines = capLines(
    wrapText(heading, sheet.bold, GROUP_HEADING_SIZE, CONTENT_WIDTH),
    sheet.bold,
    GROUP_HEADING_SIZE,
    CONTENT_WIDTH,
    linesPerPage(GROUP_HEADING_LINE_HEIGHT, CAPTION_HEIGHT + NOTE_HEIGHT + 12)
  );
  return { lines, height: lines.length * GROUP_HEADING_LINE_HEIGHT + 12 };
}

/**
 * The heading, reserved together with the whole first item under it — a
 * picture included — so a heading never ends a page with its file overleaf.
 */
function drawGroupHeading(
  sheet: PdfSheet,
  heading: ReturnType<typeof measureGroupHeading>,
  firstItemHeight: number,
  issueId: number | null
): void {
  const { lines } = heading;
  ensureSpace(sheet, heading.height + firstItemHeight);
  sheet.y -= 6;
  const top = sheet.y;
  sheet.y -= drawLines(sheet, lines, MARGIN, sheet.y, {
    font: sheet.bold,
    size: GROUP_HEADING_SIZE,
    lineHeight: GROUP_HEADING_LINE_HEIGHT,
  });
  // The issue's heading leads back to its row in the table.
  if (issueId != null) {
    sheet.links.toIssue.push({
      spot: {
        page: sheet.page,
        x: MARGIN,
        y: sheet.y,
        width: CONTENT_WIDTH,
        height: top - sheet.y,
      },
      issueId,
    });
  }
  sheet.y -= 6;
}

/** A paperclip just after text that ends at `x`, its baseline at `baseline`. */
function paperclipAfter(sheet: PdfSheet, number: number, x: number, baseline: number): void {
  sheet.links.paperclips.push({
    spot: {
      page: sheet.page,
      x: x + 4,
      y: baseline - 2,
      width: PAPERCLIP_SIZE,
      height: PAPERCLIP_SIZE,
    },
    number,
  });
}

/** Room a caption leaves at its end for the paperclip. */
const PAPERCLIP_ROOM = PAPERCLIP_SIZE + 8;

/** The caption, and the file's paperclip after it when `number` is given. */
function drawCaption(sheet: PdfSheet, text: string, number?: number): void {
  const fitted = ellipsise(text, sheet.bold, 9, CONTENT_WIDTH - PAPERCLIP_ROOM);
  const baseline = sheet.y - 11;
  drawText(sheet, fitted, MARGIN, baseline, { font: sheet.bold, size: 9 });
  if (number != null) {
    paperclipAfter(sheet, number, MARGIN + sheet.bold.widthOfTextAtSize(fitted, 9), baseline);
  }
  sheet.y -= CAPTION_HEIGHT;
}

/** A caption with one muted line under it — for a file with nothing to show. */
function drawNote(sheet: PdfSheet, caption: string, note: string, accent: boolean): void {
  ensureSpace(sheet, CAPTION_HEIGHT + NOTE_HEIGHT);
  drawCaption(sheet, caption);
  drawText(sheet, ellipsise(note, sheet.regular, 8.5, CONTENT_WIDTH), MARGIN, sheet.y - 6, {
    size: 8.5,
    color: accent ? sheet.theme.accent : MUTED,
  });
  sheet.y -= NOTE_HEIGHT + ITEM_GAP / 2;
}

/** An image at most the content width wide and `maxHeight` tall, never
 *  enlarged past one point per pixel. */
function imageSize(image: PDFImage, maxHeight: number): { width: number; height: number } {
  const scale = Math.min(1, CONTENT_WIDTH / image.width, maxHeight / image.height);
  return { width: image.width * scale, height: image.height * scale };
}

function drawImage(
  sheet: PdfSheet,
  number: number,
  caption: string,
  image: PDFImage,
  maxHeight: number
): void {
  const { width, height } = imageSize(image, maxHeight);
  ensureSpace(sheet, CAPTION_HEIGHT + height);
  // A thumbnail in the table opens here.
  sheet.links.attachmentPages.set(number, { page: sheet.page, top: sheet.y });
  drawCaption(sheet, caption, number);
  sheet.page.drawImage(image, { x: MARGIN, y: sheet.y - height, width, height });
  sheet.y -= height + ITEM_GAP;
}

/**
 * The attached PDF's pages, each on a page of its own the size of the original
 * plus a slim strip along the top naming the file — so a drawing keeps its
 * scale, and its caption is never stranded on the page before it. Nothing
 * more is drawn on the protocol page afterwards: the next item starts on a
 * fresh page after these.
 */
async function appendPdf(
  sheet: PdfSheet,
  number: number,
  caption: string,
  issueId: number | null,
  pages: readonly AttachedPage[],
  pace: Pacer
): Promise<void> {
  for (const [index, attached] of pages.entries()) {
    const shown = shownPage(attached);
    // Scaled with the sheet, so the strip reads the same on an A0 plan as on
    // an A4 letter once each is fitted to a screen.
    const scale = Math.max(1, shown.width / PAGE_WIDTH);
    const strip = STRIP_HEIGHT * scale;
    const size = 8 * scale;
    const pad = 10 * scale;
    const page = sheet.doc.addPage([shown.width, shown.height + strip]);
    sheet.foreignPages.add(page);
    if (index === 0) {
      sheet.links.attachmentPages.set(number, { page, top: shown.height + strip });
    }
    shown.drawAt({ page, x: 0, y: 0, width: shown.width, height: shown.height });
    page.drawRectangle({
      x: 0,
      y: shown.height,
      width: shown.width,
      height: strip,
      color: SURFACE,
    });
    page.drawLine({
      start: { x: 0, y: shown.height },
      end: { x: shown.width, y: shown.height },
      thickness: 0.5 * scale,
      color: RULE,
    });
    const baseline = shown.height + (strip - size) / 2 + size * 0.15;
    const counter = `${index + 1} / ${pages.length}`;
    const counterWidth = sheet.regular.widthOfTextAtSize(counter, size);
    page.drawText(counter, {
      x: shown.width - pad - counterWidth,
      y: baseline,
      font: sheet.regular,
      size,
      color: MUTED,
    });
    const clip = PAPERCLIP_SIZE * scale;
    const fitted = ellipsise(
      caption,
      sheet.bold,
      size,
      shown.width - pad * 4 - counterWidth - clip
    );
    page.drawText(toWinAnsi(fitted), { x: pad, y: baseline, font: sheet.bold, size, color: TEXT });
    const captionEnd = pad + sheet.bold.widthOfTextAtSize(fitted, size);
    sheet.links.paperclips.push({
      spot: { page, x: captionEnd + pad / 2, y: baseline - size * 0.2, width: clip, height: clip },
      number,
    });
    // The strip names the issue; clicking it goes back to the issue's row.
    if (issueId != null) {
      sheet.links.toIssue.push({
        spot: { page, x: 0, y: shown.height, width: captionEnd + pad, height: strip },
        issueId,
      });
    }
    await pace();
  }
  // Zero leaves no room on the protocol page, so the next `ensureSpace` opens a
  // new one after the appended pages — and if nothing follows, no blank page
  // is added at all.
  sheet.y = 0;
}

/** A file with no picture of its own: it is named on the list, not drawn. */
function isListed(entry: ResolvedAttachment | undefined): boolean {
  return entry?.kind === "unsupported" || entry?.kind === "failed";
}

/** What an image item takes on the page — what its heading reserves. */
function itemHeight(entry: ResolvedAttachment, shownAbove: boolean, maxImageHeight: number) {
  if (!shownAbove && entry.kind === "image") {
    return CAPTION_HEIGHT + imageSize(entry.image, maxImageHeight).height;
  }
  return CAPTION_HEIGHT + NOTE_HEIGHT;
}

function captionFor(labels: ProtocolDocumentModel["labels"], number: number, filename: string) {
  return fill(labels.attachmentCaption, { number: String(number), filename: toWinAnsi(filename) });
}

/** One line of the list: the caption, and why the file is not drawn. */
function drawListLine(
  sheet: PdfSheet,
  number: number,
  caption: string,
  note: string,
  accent: boolean
): void {
  ensureSpace(sheet, LIST_LINE_HEIGHT);
  const noteWidth = sheet.regular.widthOfTextAtSize(toWinAnsi(note), 8.5);
  const baseline = sheet.y - 9.5;
  const fitted = ellipsise(
    caption,
    sheet.regular,
    9,
    CONTENT_WIDTH - noteWidth - 12 - PAPERCLIP_ROOM
  );
  drawText(sheet, fitted, MARGIN, baseline, { size: 9 });
  // A drawing or a spreadsheet cannot be printed, but its original can still
  // travel inside the PDF.
  paperclipAfter(sheet, number, MARGIN + sheet.regular.widthOfTextAtSize(fitted, 9), baseline);
  drawText(sheet, note, MARGIN + CONTENT_WIDTH - noteWidth, baseline, {
    size: 8.5,
    color: accent ? sheet.theme.accent : MUTED,
  });
  sheet.y -= LIST_LINE_HEIGHT;
}

/**
 * The appendix: every picture and attached PDF, each under its caption and its
 * issue's heading; then, on a last page of their own, the files with no
 * picture, listed by name.
 */
export async function drawAttachments(
  sheet: PdfSheet,
  model: ProtocolDocumentModel,
  resolved: ResolvedAttachments,
  pace: Pacer
): Promise<void> {
  const groups = model.attachments?.groups ?? [];
  if (groups.length === 0) return;
  const { labels } = model;

  // A section of its own, opened on a fresh page by the first thing it draws
  // there — not up front, where an attached PDF first in line (its pages are
  // pages of their own) would leave the heading alone on a page.
  let sectionOpen = false;
  const openSection = () => {
    if (sectionOpen) return;
    sectionOpen = true;
    addPage(sheet);
    const headingSize = sheet.theme.sectionHeadingSize;
    drawSectionHeading(sheet, labels.attachments, sheet.y - headingSize);
    sheet.y -= headingSize + 16;
  };

  // The author's own attachments need a heading only when issue groups follow
  // them; on their own they are simply "the attachments".
  const labelOwn = groups.some((group) => group.heading !== null);
  const headingOf = (group: (typeof groups)[number]) =>
    group.heading ?? (labelOwn ? labels.attachmentsAdded : null);
  const groupsOf = (keep: (entry: ResolvedAttachment) => boolean) =>
    groups
      .map((group) => ({
        issueId: group.issueId ?? null,
        heading: headingOf(group),
        numbers: group.numbers.filter((number) => {
          const entry = resolved.get(number);
          return entry !== undefined && keep(entry);
        }),
      }))
      .filter((group) => group.numbers.length > 0);

  const pictured = groupsOf((entry) => !isListed(entry));
  const drawn = new Set<number>();
  for (const group of pictured) {
    const heading = group.heading ? measureGroupHeading(sheet, group.heading) : null;
    // Drawn just before the group's first item on a protocol page, reserved
    // together with it — never left at the foot of a page, nor ahead of a PDF
    // whose own pages start elsewhere. A PDF's strip names the group instead.
    let headingPending = heading !== null;
    for (const number of group.numbers) {
      const entry = resolved.get(number);
      if (!entry) continue;
      const caption = captionFor(labels, number, entry.filename);
      const shownAbove = drawn.has(number);
      if (entry.kind === "pdf" && !shownAbove) {
        const stripCaption = group.heading ? `${group.heading} · ${caption}` : caption;
        await appendPdf(sheet, number, stripCaption, group.issueId, entry.pages, pace);
        drawn.add(number);
        continue;
      }
      // The first picture under a heading shares a page with it, so it gives
      // up the heading's height — otherwise the pair is taller than any page.
      // The section's first picture likewise fits under the section heading.
      const opening = !sectionOpen;
      openSection();
      const room = opening ? sheet.y - PAGE_FOOT - CAPTION_HEIGHT : MAX_IMAGE_HEIGHT;
      const maxImageHeight = room - (headingPending ? (heading?.height ?? 0) : 0);
      if (headingPending && heading) {
        drawGroupHeading(
          sheet,
          heading,
          itemHeight(entry, shownAbove, maxImageHeight),
          group.issueId
        );
        headingPending = false;
      }
      if (shownAbove) {
        // Shared with an earlier heading: point back rather than print twice.
        drawNote(sheet, caption, labels.attachmentShownAbove, false);
      } else if (entry.kind === "image") {
        drawImage(sheet, number, caption, entry.image, maxImageHeight);
        drawn.add(number);
      }
      await pace();
    }
  }

  const listed = groupsOf(isListed);
  if (listed.length === 0) return;
  // A page of its own, after everything that has a picture.
  if (sectionOpen) addPage(sheet);
  else openSection();
  drawText(sheet, labels.attachmentsWithoutPreview, MARGIN, sheet.y - 10, {
    font: sheet.bold,
    size: 10,
  });
  sheet.y -= 20;
  for (const group of listed) {
    if (group.heading) {
      drawGroupHeading(
        sheet,
        measureGroupHeading(sheet, group.heading),
        LIST_LINE_HEIGHT,
        group.issueId
      );
    }
    for (const number of group.numbers) {
      const entry = resolved.get(number);
      if (!entry) continue;
      const failed = entry.kind === "failed";
      drawListLine(
        sheet,
        number,
        captionFor(labels, number, entry.filename),
        failed ? labels.attachmentFailed : labels.attachmentUnsupported,
        failed
      );
    }
  }
}
